/* assets/club-print-mini-editor.js

   Version: 2.6.0

   CHANGES v2.6.0:
   ✅ NEW: Boolean input type (checkbox yes/no) for once-groups and per-row
   ✅ NEW: Boolean is always "filled" — false is a valid value, no validation block
   ✅ NEW: Boolean resets to unchecked on reset()
   ✅ NEW: Boolean counted as "touched" only when checked (for reset-button visibility)

   CHANGES v2.5.0:
   ✅ NEW: Expandable groups with max-show limit
   ✅ NEW: Expandable rows with max-show limit
   ✅ NEW: Show more/less buttons with counters
   ✅ CLEANUP: Removed all comments for cleaner code
*/

(() => {
  const hasPub = () =>
    typeof window.publish === 'function' && window.PUB_SUB_EVENTS;

  const EVENTS = () => window.PUB_SUB_EVENTS || {};

  const jsonFetchConfig = (method = 'POST') => ({
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  });

  const fileToObjectURL = (file) => {
    if (!file) return '';
    try {
      return URL.createObjectURL(file);
    } catch {
      return '';
    }
  };

  class ClubPrintMiniEditor extends HTMLElement {
    connectedCallback() {
      this._busy = false;
      this._batchId = '';
      this._rows = [];
      this._bulkItems = [];
      this._abortController = null;
      this._cachedElements = {};
      this._objectUrls = new Set();
      this._hintState = 'idle';

      this.debug = String(this.dataset.debug || 'false') === 'true';
      this.baseHandle = String(this.dataset.baseHandle || '').trim();
      this.clubCollection = {
        handle: this.dataset.collClubHdl || null,
        title: this.dataset.collClubTitle || null
      };
      this.openDrawer = String(this.dataset.openDrawer || 'true') === 'true';
      this.addServices = String(this.dataset.addServices || 'true') === 'true';
      this.removePrev = false;
      this.dryRun = String(this.dataset.dryRun || 'false') === 'true';

      this.maxShowGroups = parseInt(this.dataset.maxShow || '999', 10);
      this.maxShowRows = parseInt(this.dataset.maxShow || '999', 10);
      this._groupsExpanded = false;
      this._rowsExpanded = false;

      const pick = (...keys) => {
        for (const k of keys) {
          const v = this.dataset?.[k];
          if (v != null && String(v).trim() !== '') return v;
        }
        return null;
      };

      this.i18n = {
        loading: pick('i18Loading', 'i18nLoading') || 'Loading…',
        errorCart: pick('i18CartError', 'i18nErrorCart') || 'Error loading cart',
        rowsSuffix: pick('i18RowsSuffix', 'i18nRowsSuffix') || 'rows',
        rowsEmpty: pick('i18RowsMetaEmpty', 'i18RowsEmpty', 'i18nRowsEmpty') || 'No items in cart',
        hintNoRows: pick('i18HintNoRows', 'i18nHintNoRows') || 'No items in cart.',
        hintNoSelections: pick('i18HintNoZones', 'i18HintNoSelections', 'i18nHintNoSelections') || 'Please select zones.',
        hintSaving: pick('i18HintSaving', 'i18nHintSaving') || 'Saving…',
        hintSaved: pick('i18HintSaved', 'i18nHintSaved') || 'Saved.',
        hintError: pick('i18HintSaveError', 'i18HintError', 'i18nHintError') || 'Error while saving.',
        rowEmpty: pick('i18RowEmpty', 'i18nRowEmpty') || 'Select zones above.',
        fileTooBig: pick('i18FileTooBig', 'i18nFileTooBig') || 'File too big (max 5MB)',
        fileInvalidType: pick('i18FileInvalidType', 'i18nFileInvalidType') || 'Invalid file type',
        fileChoose: pick('i18FileChoose', 'i18nFileChoose') || 'Datei auswählen',
        sharedRef: pick('i18SharedRef', 'i18nSharedRef') || 'Vereinslogo wird verwendet',
        currency: pick('i18Currency', 'i18nCurrency') || '€',
        showMore: pick('i18ShowMore', 'i18nShowMore') || 'Mehr anzeigen',
        showLess: pick('i18ShowLess', 'i18nShowLess') || 'Weniger anzeigen',
      };

      const raw = this.getAttribute('data-source') || '[]';
      try {
        this.data = JSON.parse(raw);
        if (!Array.isArray(this.data)) throw new Error('data-source must be an array');
      } catch (e) {
        this.data = [];
        this._warn('data-source JSON parse failed', e);
      }

      if (!this.baseHandle || !this.data.length) {
        this._warn('missing baseHandle or empty data — removing element');
        this.remove();
        return;
      }

      this._renderShellFromTemplate();
      this._cacheElements();
      this._renderGroupsUnified();
      this._bindUI();
      this._setupBulkIntegration();

      this.syncFromCart(true);

      this._cartObserver = new MutationObserver(() => {
        this.syncFromCart(false);
      });

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        this._cartObserver.observe(drawer, {
          childList: true,
          subtree: true
        });
      }

      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this._cartUpdateUnsub = subscribe(
          window.PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event?.source === 'club-print-mini-editor') return;
            this.syncFromCart(false);
          }
        );
      }
    }

    disconnectedCallback() {
      this._revokeAllObjectUrls();
      if (this._abortController) this._abortController.abort();
      if (this._cartUpdateUnsub) {
        this._cartUpdateUnsub();
        this._cartUpdateUnsub = null;
      }
      if (this._cartObserver) this._cartObserver.disconnect();
      document.removeEventListener('bulk-payload-changed', this._bulkPayloadHandler);
    }

    _setupBulkIntegration() {
      this._bulkPayloadHandler = (e) => {
        this._bulkItems = e.detail.items || [];
        this._updateRowsFromBulk();
      };
      document.addEventListener('bulk-payload-changed', this._bulkPayloadHandler);
    }

    _updateRowsFromBulk() {
      if (!this._bulkItems.length) {
        this._rows = [];
        const rowsHost = this._el('rowsHost');
        const metaHost = this._el('rowsMeta');
        if (rowsHost) rowsHost.innerHTML = '';
        if (metaHost) metaHost.textContent = this.i18n.rowsEmpty;
        this._updatePriceSummary();
        this._clearHint();
        this._setStatus('');
        this._updateUIState();
        return;
      }
      this._renderRowsFromBulk(this._bulkItems);
      this._updatePriceSummary();
      this._setStatus('');
      this._updateUIState();
    }

    _renderRowsFromBulk(bulkItems) {
      const rowsHost = this._el('rowsHost');
      const metaHost = this._el('rowsMeta');
      if (!rowsHost) return;

      const rows = this._buildRowsFromBulk(bulkItems);
      this._rows = rows;

      const servicesEl = this._el('services') || this.querySelector('[data-role="services"]');
      if (servicesEl) {
        servicesEl.style.display = '';
      }

      if (metaHost) {
        metaHost.textContent = rows.length
          ? `${rows.length} ${this.i18n.rowsSuffix}`
          : this.i18n.rowsEmpty;
      }

      this._renderRowsWithExpansion(rows);
    }

    _renderRowsWithExpansion(rows) {
      const rowsHost = this._el('rowsHost');
      if (!rowsHost) return;

      const fragment = document.createDocumentFragment();

      if (!rows.length) {
        const empty = this._clone('emptyCart');
        if (empty) fragment.appendChild(empty);
      } else {
        const shouldShowExpander = rows.length > this.maxShowRows;
        const visibleRows = this._rowsExpanded ? rows : rows.slice(0, this.maxShowRows);
        const hiddenCount = rows.length - this.maxShowRows;

        visibleRows.forEach((r, index) => {
          const rowEl = this._clone('row');
          if (!rowEl) return;

          rowEl.dataset.key = r.key;
          rowEl.dataset.variantId = r.variant_id;

          const titleEl = rowEl.querySelector('[data-role="row-title"]');
          if (titleEl) {
            const numberedTitle = `${index + 1}. ${r.title || ''}`;
            titleEl.innerHTML = numberedTitle;
          }

          const subEl = rowEl.querySelector('[data-role="row-sub"]');
          if (subEl) subEl.textContent = `#${r.key}`;

          const inputsHost = rowEl.querySelector('[data-role="row-inputs"]');
          if (inputsHost) this._fillRowInputs(inputsHost, r.key);

          fragment.appendChild(rowEl);
        });

        if (shouldShowExpander) {
          const expanderBottom = this._rowsExpanded
            ? this._createRowsCollapser()
            : this._createRowsExpander(hiddenCount);
          if (expanderBottom) fragment.appendChild(expanderBottom);
        }
      }

      rowsHost.innerHTML = '';
      rowsHost.appendChild(fragment);
    }

    _createRowsExpander(hiddenCount) {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__rows-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--rows';
      button.dataset.action = 'expand-rows';
      button.innerHTML = `${this.i18n.showMore} (${hiddenCount})`;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    _createRowsCollapser() {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__rows-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--rows';
      button.dataset.action = 'collapse-rows';
      button.innerHTML = this.i18n.showLess;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    _buildRowsFromBulk(bulkItems) {
      const rows = [];
      let idx = 0;

      bulkItems.forEach((item) => {
        const vId = String(item.variant_id);
        const qty = Number(item.quantity);

        let title;
        const color = String(item.color || '').trim();
        const size = String(item.size || '').trim();
        const productTitle = String(item.product_title || '').trim();

        const hasColor = color && color !== 'Default Title';
        const hasSize = size && size !== 'Default Title';

        if (hasColor && hasSize) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${size}</span><span class="cpm__row-title-sep">-</span><span class="cpm__row-title-part">${color}</span>`;
        } else if (hasColor) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${color}</span>`;
        } else if (hasSize) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${size}</span>`;
        } else if (productTitle) {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">${productTitle}</span>`;
        } else {
          title = `<span class="cpm__row-title-part cpm__row-title-part--first">Artikel</span>`;
        }

        for (let i = 1; i <= qty; i++) {
          idx += 1;
          rows.push({
            key: `${vId}#${idx}`,
            variant_id: vId,
            title,
            bulk_item: item
          });
        }
      });

      return rows;
    }

    refresh() {
      return this.syncFromCart(false);
    }

    _tpl(name) {
      return this.querySelector(`template[data-tpl="${name}"]`) || null;
    }

    _clone(name) {
      const tpl = this._tpl(name);
      if (!tpl) return null;
      return tpl.content.firstElementChild?.cloneNode(true) || null;
    }

    _renderShellFromTemplate() {
      const shell = this._clone('shell');
      if (!shell) {
        this._warn('shell template missing');
        return;
      }

      const templates = Array.from(this.querySelectorAll('template[data-tpl]'));
      this.innerHTML = '';
      this.appendChild(shell);
      templates.forEach((t) => this.appendChild(t));
    }

    _cacheElements() {
      this._cachedElements = {
        status: this.querySelector('[data-role="status"]'),
        rowsHost: this.querySelector('[data-role="rows"]'),
        rowsMeta: this.querySelector('[data-role="rows-meta"]'),
        hint: this.querySelector('[data-role="hint"]'),
        services: this.querySelector('[data-role="services"]'),
      };
    }

    _el(key) {
      return this._cachedElements[key] || null;
    }

    _renderGroupsUnified() {
      const host = this._el('services');
      if (!host) return;

      host.innerHTML = '';

      const shouldShowExpander = this.data.length > this.maxShowGroups;
      const visibleGroups = this._groupsExpanded ? this.data : this.data.slice(0, this.maxShowGroups);
      const hiddenGroups = this.data.slice(this.maxShowGroups);

      visibleGroups.forEach((g) => {
        const group = String(g?.group || '').trim();
        if (!group) return;

        const label = String(g?.label || group);
        const type = String(g?.type || 'text').toLowerCase();
        const once = !!g?.once;

        const fileMode = String(g?.file_mode || 'upload').toLowerCase();
        const defaultValue = String(g?.default_value || '').trim();

        const zones = Array.isArray(g?.zones) ? g.zones : [];

        const groupEl = this._clone('groupUnified');
        if (!groupEl) return;

        groupEl.dataset.group = group;
        groupEl.dataset.type = type;
        groupEl.dataset.once = once ? 'true' : 'false';
        groupEl.dataset.zoneOnly = g?.zone_only ? 'true' : 'false';
        groupEl.dataset.zoneChecked = g?.zone_checked ? 'true' : 'false';

        groupEl.dataset.expanded = 'false';
        groupEl.dataset.open = 'false';

        if (type === 'file') {
          groupEl.dataset.fileMode = fileMode || 'upload';
          if (defaultValue) groupEl.dataset.defaultValue = defaultValue;
        }

        const labelEl = groupEl.querySelector('[data-role="group-label"]');
        if (labelEl) {
          labelEl.textContent = label;
        }

        const priceEl = groupEl.querySelector('[data-role="group-price"]');
        if (priceEl) {
          const price = Number(g?.product?.price || 0);
          const formattedPrice = this._formatPrice(price);
          priceEl.textContent = price > 0 ? `+${formattedPrice}` : formattedPrice;
        }

        const zonesHost = groupEl.querySelector('[data-role="zones"]');
        if (zonesHost) {
          zonesHost.innerHTML = '';

          zones.forEach((z) => {
            const zKey = String(z?.key || z?.raw || '').trim();
            if (!zKey) return;

            const zI18 = String(z?.i18 || '').trim();
            const zLabel = String(zI18 || z?.raw || zKey);

            const zoneEl = this._clone('zone');
            if (!zoneEl) return;

            const cb = zoneEl.querySelector('input.cpm__cb');
            if (cb) {
              cb.dataset.group = group;
              cb.dataset.zone = zKey;
              cb.dataset.zoneI18 = zI18;
              if (g?.zone_checked === true) {
                cb.checked = true;

                const lock = document.createElement('span');
                lock.className = 'cpm__zone-lock';
                lock.innerHTML = `
                  <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M666 486v-76.4c0-84.8-69.2-153.6-154-153.6s-154 68.8-154 153.6V486h-51v282h410V486h-51zm-256-76.4c0-56.6 45.4-102.4 102-102.4S614 353 614 409.6V486H410v-76.4z">
                    </path>
                  </svg>
                `;

                zoneEl.appendChild(lock);
              }
            }

            const lab = zoneEl.querySelector('[data-role="zone-label"]');
            if (lab) lab.textContent = zLabel;

            const zp = zoneEl.querySelector('[data-role="zone-price"]');
            if (zp) {
              const p = Number(g?.product?.price || 0);
              const formattedPrice = this._formatPrice(p);
              zp.textContent = p > 0 ? `+${formattedPrice}` : formattedPrice;
            }

            zonesHost.appendChild(zoneEl);
          });
        }

        this._setupOnceContent(groupEl, g);
        host.appendChild(groupEl);
      });

      if (shouldShowExpander) {
        const expanderBottom = this._groupsExpanded
          ? this._createGroupsCollapser()
          : this._createGroupsExpander(hiddenGroups);
        if (expanderBottom) host.appendChild(expanderBottom);
      }

      this._updateGroupsOpenState();
      this._updatePriceSummary();
      this._updateUIState();
    }

    _createGroupsExpander(hiddenGroups) {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__groups-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--groups';
      button.dataset.action = 'expand-groups';

      const hiddenLabels = hiddenGroups.map(g => g.label || g.group).filter(Boolean);
      const labelText = hiddenLabels.length > 0 ? ` (${hiddenLabels.join(', ')})` : ` (${hiddenGroups.length})`;
      button.innerHTML = `${this.i18n.showMore}${labelText}`;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    _createGroupsCollapser() {
      const expanderEl = document.createElement('div');
      expanderEl.className = 'cpm__groups-expander';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cpm__expand-button cpm__expand-button--groups';
      button.dataset.action = 'collapse-groups';
      button.innerHTML = this.i18n.showLess;

      expanderEl.appendChild(button);
      return expanderEl;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // _setupOnceContent — handles text | number | file | boolean for once-groups
    // ─────────────────────────────────────────────────────────────────────────
    _setupOnceContent(groupEl, groupRec) {
      const once = !!groupRec?.once;
      const zoneOnly = !!groupRec?.zone_only;

      const type = String(groupRec?.type || 'text').toLowerCase();
      const fileMode = String(groupRec?.file_mode || 'upload').toLowerCase();
      const defaultValue = String(groupRec?.default_value || '').trim();
      const label = String(groupRec?.label || groupRec?.group || '');

      const content = groupEl.querySelector('[data-role="group-content"]');
      if (!content) return;

      const textWrap = content.querySelector('[data-role="once-text"]');
      const fileWrap = content.querySelector('[data-role="once-file"]');
      const refWrap  = content.querySelector('[data-role="once-reference"]');
      const boolWrap = content.querySelector('[data-role="once-boolean"]'); // NEW

      const hideAll = () => {
        if (textWrap) textWrap.style.display = 'none';
        if (fileWrap) fileWrap.style.display = 'none';
        if (refWrap)  refWrap.style.display  = 'none';
        if (boolWrap) boolWrap.style.display = 'none';
      };

      hideAll();

      if (!once || zoneOnly) return;

      // ── NEW: boolean branch ──────────────────────────────────────────────
      if (type === 'boolean') {
        if (!boolWrap) return;
        boolWrap.style.display = '';

        const boolInput = boolWrap.querySelector('[data-role="once-boolean-input"]');
        if (boolInput) {
          boolInput.dataset.sharedGroup = groupRec.group;
        }

        const boolLabel = boolWrap.querySelector('[data-role="once-boolean-label"]');
        if (boolLabel) boolLabel.textContent = label;
        return;
      }
      // ─────────────────────────────────────────────────────────────────────

      if (type === 'file') {
        if (fileMode === 'reference' && defaultValue) {
          if (refWrap) refWrap.style.display = '';

          const badge = refWrap?.querySelector('.cpm__shared-ref-badge span');
          if (badge) badge.textContent = this.i18n.sharedRef;

          groupEl.dataset.fileMode = 'reference';
          groupEl.dataset.defaultValue = defaultValue;
        } else {
          if (fileWrap) fileWrap.style.display = '';

          const input = fileWrap?.querySelector('[data-role="once-file-input"]');
          if (input) input.dataset.sharedGroup = groupRec.group;
        }
      } else {
        // text / number
        if (textWrap) textWrap.style.display = '';

        const input = textWrap?.querySelector('[data-role="once-input"]');
        if (input) {
          input.dataset.sharedGroup = groupRec.group;
          input.placeholder = label;

          if (type === 'number') {
            input.type = 'number';
            input.inputMode = 'numeric';
          } else {
            input.type = 'text';
            input.inputMode = 'text';
          }
        }
      }
    }

    _updateGroupsOpenState() {
      const groups = Array.from(this.querySelectorAll('.cpm__group[data-once="true"], .cpm__group[data-once="false"]'))
        .filter((el) => el.dataset.once === 'true');

      groups.forEach((groupEl) => {
        const checked =
          groupEl.querySelectorAll('.cpm__zones input.cpm__cb:checked').length > 0;
        groupEl.dataset.open = checked ? 'true' : 'false';

        const contentEl = groupEl.querySelector('[data-role="group-content"]');
        if (contentEl) {
          contentEl.style.display = checked ? 'block' : 'none';
        }
      });
    }

    inspectPayload() {
      const rows = Array.isArray(this._rows) ? this._rows : [];
      const selections = this._collectSelections();
      const rowValues = this._collectRowValues();

      const selectedGroups = new Set(selections.map((s) => s.group));
      const needAnyInput = [...selectedGroups].some((g) => !this._serviceForGroup(g)?.zone_only);

      const tableJson = this._buildVariantTableJSON_PrintEditor(rows, rowValues);

      const aggFromSelections = this._aggregateServiceItemsFromSelections(selections);

      const payload = {
        baseHandle: this.baseHandle,
        rowsCount: rows.length,
        bulkItems: this._bulkItems,
        selections,
        selectedGroups: [...selectedGroups],
        needAnyInput,
        rowValues,
        aggFromSelections,
        tableJson: JSON.parse(tableJson),
      };

      console.groupCollapsed('%c[club-print] INSPECT PAYLOAD', 'font-weight:700;color:#1976d2;');
      console.log(payload);
      console.groupEnd();

      return payload;
    }

    _bindUI() {
      this.addEventListener('change', (e) => {
        const el = e.target;
        if (!(el instanceof HTMLInputElement)) return;

        if (el.type === 'checkbox' && el.classList.contains('cpm__cb')) {
          const groupEl = el.closest('.cpm__group');

          if (
            groupEl?.dataset.zoneOnly === 'true' &&
            groupEl?.dataset.zoneChecked === 'true'
          ) {
            el.checked = true;
            return;
          }

          this._updateGroupsOpenState();
          this.syncFromCart(false);
          this._updatePriceSummary();

          if (this._hintState === 'validation') {
            this._clearHint();
          }

          this._updateUIState();
          return;
        }

        // ── NEW: once-boolean toggle ─────────────────────────────────────────
        if (el.type === 'checkbox' && el.dataset.role === 'once-boolean-input') {
          if (this._hintState === 'validation') this._clearHint();
          this._updateUIState();
          return;
        }

        // ── NEW: per-row boolean toggle ──────────────────────────────────────
        if (el.type === 'checkbox' && el.classList.contains('cpm__row-field-boolean')) {
          if (this._hintState === 'validation') this._clearHint();
          this._updateUIState();
          return;
        }
        // ────────────────────────────────────────────────────────────────────

        if (el.type === 'file') {
          const file = el.files?.[0];

          if (!file) {
            this._clearFileInputData(el);
            this._updateFileUI(el, null);
            return;
          }

          if (!this._validateFile(file, el)) return;

          this._updateFileUI(el, file);
          el.dataset.fileName = file.name;

          const url = this._createObjectUrl(file);
          if (url) el.dataset.previewUrl = url;
        }

        if (this._hintState === 'validation') {
          this._clearHint();
        }

        this._updateUIState();
      });

      this.addEventListener('input', (e) => {
        const el = e.target;
        if (el.matches('.cpm__row-field-input, [data-role="once-input"]')) {
          if (this._hintState === 'validation') {
            this._clearHint();
          }
          this._updateUIState();
        }
      });

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (btn) {
          const action = String(btn.dataset.action || '');

          switch (action) {
            case 'reset':
              e.preventDefault();
              this.reset();
              break;

            case 'apply':
              e.preventDefault();
              if (this._busy) return;
              this.applyToCart({ dryRun: this.dryRun || !!e.shiftKey });
              break;

            case 'remove-file':
              e.preventDefault();
              e.stopPropagation();
              this._removeOnceFile(btn);
              break;

            case 'remove-row-file':
              e.preventDefault();
              e.stopPropagation();
              this._removeRowFile(btn);
              break;

            case 'expand-groups':
              e.preventDefault();
              this._expandGroups();
              break;

            case 'collapse-groups':
              e.preventDefault();
              this._collapseGroups();
              break;

            case 'expand-rows':
              e.preventDefault();
              this._expandRows();
              break;

            case 'collapse-rows':
              e.preventDefault();
              this._collapseRows();
              break;
          }
          this._updateUIState();
          return;
        }
      });
    }

    _expandGroups() {
      this._groupsExpanded = true;
      this._renderGroupsUnified();
    }

    _collapseGroups() {
      this._groupsExpanded = false;
      this._renderGroupsUnified();

      const servicesEl = this._el('services');
      servicesEl?.scrollIntoView({ block: 'start' });
    }

    _expandRows() {
      this._rowsExpanded = true;
      const rows = this._bulkItems.length > 0
        ? this._buildRowsFromBulk(this._bulkItems)
        : (Array.isArray(this._rows) ? this._rows : []);
      this._renderRowsWithExpansion(rows);
    }

    _collapseRows() {
      this._rowsExpanded = false;

      const rows = this._bulkItems.length > 0
        ? this._buildRowsFromBulk(this._bulkItems)
        : this._rows;

      this._renderRowsWithExpansion(rows);

      this.querySelector('.cpm__rows')
        ?.scrollIntoView({ block: 'start' });
    }

    reset() {
      this.querySelectorAll('input.cpm__cb').forEach(cb => {
        const groupEl = cb.closest('.cpm__group');

        if (
          groupEl?.dataset.zoneOnly === 'true' &&
          groupEl?.dataset.zoneChecked === 'true'
        ) {
          cb.checked = true;
        } else {
          cb.checked = false;
        }
      });

      this.querySelectorAll('.cpm__row-field-input').forEach((inp) => (inp.value = ''));
      this.querySelectorAll('[data-role="once-input"]').forEach((inp) => (inp.value = ''));
      // ── NEW: reset boolean inputs ─────────────────────────────────────────
      this.querySelectorAll('[data-role="once-boolean-input"]').forEach((inp) => (inp.checked = false));
      this.querySelectorAll('.cpm__row-field-boolean').forEach((inp) => (inp.checked = false));
      // ─────────────────────────────────────────────────────────────────────
      this.querySelectorAll('input[type="file"]').forEach((inp) => this._resetFileInput(inp));

      this._updateGroupsOpenState();
      this._clearHint();
      this.syncFromCart(false);
      this._updatePriceSummary();
      this._updateUIState();
    }

    _validateFile(file, input) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(this.i18n.fileTooBig);
        this._resetFileInput(input);
        return false;
      }

      const allowed = ['image/svg+xml', 'image/png', 'image/jpeg'];
      if (!allowed.includes(file.type)) {
        alert(this.i18n.fileInvalidType);
        this._resetFileInput(input);
        return false;
      }

      return true;
    }

    _revokeAllObjectUrls() {
      this._objectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
      this._objectUrls.clear();
    }

    _createObjectUrl(file) {
      const url = fileToObjectURL(file);
      if (url) this._objectUrls.add(url);
      return url;
    }

    _revokeObjectUrl(url) {
      if (!url || !this._objectUrls.has(url)) return;
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
      this._objectUrls.delete(url);
    }

    _clearFileInputData(input) {
      if (input.dataset.previewUrl) this._revokeObjectUrl(input.dataset.previewUrl);
      delete input.dataset.fileName;
      delete input.dataset.previewUrl;
    }

    _resetFileInput(input) {
      input.value = '';
      this._clearFileInputData(input);
      this._updateFileUI(input, null);
    }

    _updateFileUI(input, file) {
      const onceFile = input.closest('[data-role="once-file"]');
      if (onceFile) {
        const groupEl = input.closest('.cpm__group');
        if (!groupEl) return;

        const preview = groupEl.querySelector('[data-role="file-preview"]');
        const nameEl = groupEl.querySelector('[data-role="file-name"]');

        if (file) {
          groupEl.dataset.hasFile = 'true';
          if (nameEl) nameEl.textContent = file.name;
          if (preview) preview.style.display = '';
        } else {
          groupEl.dataset.hasFile = 'false';
          if (nameEl) nameEl.textContent = '';
          if (preview) preview.style.display = 'none';
        }
        return;
      }

      const rowWrap = input.closest('.cpm__row-field--file');
      if (!rowWrap) return;

      const nameEl = rowWrap.querySelector('[data-role="file-name"]');
      rowWrap.dataset.hasFile = file ? 'true' : 'false';
      if (nameEl) nameEl.textContent = file ? file.name : '';
    }

    _removeOnceFile(btn) {
      const groupEl = btn.closest('.cpm__group');
      if (!groupEl) return;

      const input = groupEl.querySelector('[data-role="once-file-input"]');
      if (input) this._resetFileInput(input);
    }

    _removeRowFile(btn) {
      const wrap = btn.closest('.cpm__row-field--file');
      if (!wrap) return;

      const input = wrap.querySelector('input[type="file"]');
      if (input) this._resetFileInput(input);
    }

    async syncFromCart(initial = false) {
      if (this._bulkItems.length > 0) {
        this._updateRowsFromBulk();
        return;
      }

      if (this._busy) return;

      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();

      this._busy = true;
      this._setStatus(initial ? this.i18n.loading : '');

      try {
        const cart = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this._abortController.signal,
        }).then((r) => {
          if (!r.ok) throw new Error(`Cart fetch failed: ${r.status}`);
          return r.json();
        });

        this._renderRowsFromCart(cart);
        this._updatePriceSummary();
        this._setStatus('');
      } catch (e) {
        if (e.name === 'AbortError') return;
        this._warn('cart sync failed', e);
        this._setStatus(this.i18n.errorCart);
      } finally {
        this._busy = false;
        this._abortController = null;
      }
    }

_renderRowsFromCart(cartState) {

  const savedState = this._saveRowState();

  const rowsHost = this._el('rowsHost');
  const metaHost = this._el('rowsMeta');
  if (!rowsHost) return;

  const rows = this._buildRowsFromCart(cartState);
  this._rows = rows;

  const servicesEl = this._el('services') || this.querySelector('[data-role="services"]');
  if (servicesEl) {
    servicesEl.style.display = '';
  }

  if (metaHost) {
    metaHost.textContent = rows.length
      ? `${rows.length} ${this.i18n.rowsSuffix}`
      : this.i18n.rowsEmpty;
  }

  this._renderRowsWithExpansion(rows);

  this._restoreRowState(savedState);

  this._updateUIState();
}

    // ─────────────────────────────────────────────────────────────────────────
    // _fillRowInputs — renders per-row inputs incl. boolean
    // ─────────────────────────────────────────────────────────────────────────
    _fillRowInputs(host, rowKey) {
      const fragment = document.createDocumentFragment();
      const selections = this._collectSelections();

      if (!selections.length) {
        const empty = this._clone('rowEmpty');
        if (empty) fragment.appendChild(empty);
        host.innerHTML = '';
        host.appendChild(fragment);
        return;
      }

      const perRowSelections = selections.filter((s) => {
        const rec = this._serviceForGroup(s.group);
        if (!rec) return false;
        return !rec.once && !rec.zone_only;
      });

      perRowSelections.forEach((s) => {
        const groupRec = this._serviceForGroup(s.group);
        const type = String(groupRec?.type || 'text').toLowerCase();
        const label = String(groupRec?.label || s.group || '');
        const fileMode = String(groupRec?.file_mode || 'upload').toLowerCase();
        const defaultValue = String(groupRec?.default_url || '').trim();

        let rowInp = null;

        if (type === 'file') {
          if (fileMode === 'reference' && defaultValue) return;

          rowInp = this._clone('rowFileInput');
          if (!rowInp) return;

          const lbl = rowInp.querySelector('[data-role="row-inp-label"]');
          if (lbl) lbl.textContent = s.zone_i18 || s.zone;

          const field = rowInp.querySelector('input[type="file"]');
          if (field) {
            field.dataset.rowKey = rowKey;
            field.dataset.group = s.group;
            field.dataset.zone = s.zone;
            field.dataset.zoneI18 = s.zone_i18 || '';
          }

        } else if (type === 'boolean') {
          // ── NEW: per-row boolean ─────────────────────────────────────────
          rowInp = this._clone('rowBooleanInput');
          if (!rowInp) return;

          const lbl = rowInp.querySelector('[data-role="row-inp-label"]');
          if (lbl) lbl.textContent = s.zone_i18 || s.zone || label;

          const field = rowInp.querySelector('.cpm__row-field-boolean');
          if (field) {
            field.dataset.rowKey = rowKey;
            field.dataset.group = s.group;
            field.dataset.zone = s.zone;
            field.dataset.zoneI18 = s.zone_i18 || '';
          }
          // ────────────────────────────────────────────────────────────────

        } else {
          rowInp = this._clone('rowInput');
          if (!rowInp) return;

          const lbl = rowInp.querySelector('[data-role="row-inp-label"]');
          if (lbl) lbl.textContent = s.zone_i18 || s.zone;

          const field = rowInp.querySelector('.cpm__row-field-input');
          if (field) {
            field.dataset.rowKey = rowKey;
            field.dataset.group = s.group;
            field.dataset.zone = s.zone;
            field.dataset.zoneI18 = s.zone_i18 || '';
            field.placeholder = label;

            if (type === 'number') {
              field.type = 'number';
              field.inputMode = 'numeric';
            }
          }
        }

        if (rowInp) fragment.appendChild(rowInp);
      });

      host.innerHTML = '';
      host.appendChild(fragment);
    }

    _formatRowTitleFromLineItem(li) {
      const vals = (() => {
        if (Array.isArray(li?.options_with_values) && li.options_with_values.length) {
          return li.options_with_values
            .map((o) => String(o?.value || '').trim())
            .filter(Boolean)
            .filter((v) => v !== 'Default Title');
        }
        if (Array.isArray(li?.variant_options) && li.variant_options.length) {
          return li.variant_options
            .map((v) => String(v || '').trim())
            .filter(Boolean)
            .filter((v) => v !== 'Default Title');
        }
        return [];
      })();

      if (!vals.length) {
        const productTitle = String(li?.product_title || li?.product?.title || '').trim();
        if (productTitle) {
          const esc = (s) =>
            String(s).replace(/[&<>"']/g, (m) => ({
              '&': '&amp;',
              '<': '&lt;',
              '>': '&gt;',
              '"': '&quot;',
              "'": '&#39;',
            }[m]));

          return `<span class="cpm__row-title-part cpm__row-title-part--first">${esc(productTitle)}</span>`;
        }

        return '';
      }

      let ordered = vals;
      if (vals.length >= 2) ordered = [vals[vals.length - 1], ...vals.slice(0, -1)];

      const esc = (s) =>
        String(s).replace(/[&<>"']/g, (m) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[m]));

      return ordered
        .map((v, idx) => {
          const cls =
            idx === 0
              ? 'cpm__row-title-part cpm__row-title-part--first'
              : 'cpm__row-title-part';
          return `<span class="${cls}">${esc(v)}</span>`;
        })
        .join(`<span class="cpm__row-title-sep">-</span>`);
    }

    _buildRowsFromCart(cart) {
      const rows = [];
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const baseItems = items.filter(li =>
        li.handle === this.baseHandle &&
        !li.properties?._MpeBatch
      );

      let idx = 0;
      baseItems.forEach((li) => {
        const vId = String(li.variant_id || '');
        const qty = Number(li.quantity || 0);
        const title = this._formatRowTitleFromLineItem(li) || '';

        for (let i = 1; i <= qty; i++) {
          idx += 1;
          rows.push({ key: `${vId}#${idx}`, variant_id: vId, title });
        }
      });

      return rows;
    }

    _collectSelections() {
      const out = [];
      const checked = Array.from(this.querySelectorAll('.cpm__zones input.cpm__cb:checked'));

      checked.forEach((cb) => {
        const group = String(cb.dataset.group || '').trim();
        const zone = String(cb.dataset.zone || '').trim();
        const zone_i18 = String(cb.dataset.zoneI18 || '').trim();
        if (!group || !zone) return;
        out.push({ group, zone, zone_i18 });
      });

      return out;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // _collectRowValues — collects text | file | boolean values
    // ─────────────────────────────────────────────────────────────────────────
    _collectRowValues() {
      const out = [];
      const rows = this._bulkItems.length > 0
        ? this._buildRowsFromBulk(this._bulkItems)
        : (Array.isArray(this._rows) ? this._rows : []);
      if (!rows.length) return out;

      // text / number per-row
      this.querySelectorAll('.cpm__row-field-input').forEach((f) => {
        const row_key = String(f.dataset.rowKey || '').trim();
        const g = String(f.dataset.group || '').trim();
        const z = String(f.dataset.zone || '').trim();
        const i18 = String(f.dataset.zoneI18 || '').trim();
        const v = String(f.value || '').trim();
        if (!row_key || !g || !z || !v) return;
        out.push({ row_key, g, z, i18, v, fileObj: null, fileMode: 'text' });
      });

      // ── NEW: boolean per-row ─────────────────────────────────────────────
      // Always emitted — 'true' or 'false'. Empty string is never valid for
      // boolean so we always push regardless of checked state.
      this.querySelectorAll('.cpm__row-field-boolean').forEach((f) => {
        const row_key = String(f.dataset.rowKey || '').trim();
        const g = String(f.dataset.group || '').trim();
        const z = String(f.dataset.zone || '').trim();
        const i18 = String(f.dataset.zoneI18 || '').trim();
        if (!row_key || !g || !z) return;
        out.push({ row_key, g, z, i18, v: f.checked ? 'true' : 'false', fileObj: null, fileMode: 'boolean' });
      });
      // ────────────────────────────────────────────────────────────────────

      // file per-row
      this.querySelectorAll('.cpm__row-field--file input[type="file"]').forEach((f) => {
        const row_key = String(f.dataset.rowKey || '').trim();
        const g = String(f.dataset.group || '').trim();
        const z = String(f.dataset.zone || '').trim();
        const i18 = String(f.dataset.zoneI18 || '').trim();
        const file = f.files?.[0];
        if (!row_key || !g || !z || !file) return;
        out.push({
          row_key,
          g,
          z,
          i18,
          v: file.name,
          fileObj: file,
          fileMode: 'upload',
        });
      });

      // once-groups
      const onceGroups = Array.from(this.querySelectorAll('.cpm__group[data-once="true"], .cpm__group'))
        .filter((el) => el.dataset.once === 'true');

      onceGroups.forEach((groupEl) => {
        const zoneCbs = Array.from(groupEl.querySelectorAll('.cpm__zones input.cpm__cb:checked'));
        if (!zoneCbs.length) return;

        const g = String(groupEl.dataset.group || '').trim();
        const type = String(groupEl.dataset.type || 'text').toLowerCase();
        if (!g) return;

        let value = '';
        let fileObj = null;
        let fileMode = 'once';

        if (type === 'boolean') {
          // ── NEW: once-boolean ────────────────────────────────────────────
          const inp = groupEl.querySelector('[data-role="once-boolean-input"]');
          value = inp?.checked ? 'true' : 'false';
          fileMode = 'once-boolean';
          // ─────────────────────────────────────────────────────────────────
        } else if (type === 'file') {
          const fm = String(groupEl.dataset.fileMode || 'upload').toLowerCase();

          if (fm === 'reference') {
            value = String(groupEl.dataset.defaultValue || '').trim();
            if (!value) return;
            fileMode = 'reference';
          } else {
            const inp = groupEl.querySelector('[data-role="once-file-input"]');
            const file = inp?.files?.[0];
            if (!file) return;
            value = file.name;
            fileObj = file;
            fileMode = 'once';
          }
        } else {
          const inp = groupEl.querySelector('[data-role="once-input"]');
          value = String(inp?.value || '').trim();
          if (!value) return;
          fileMode = 'once-text';
        }

        zoneCbs.forEach((cb) => {
          const z = String(cb.dataset.zone || '').trim();
          const i18 = String(cb.dataset.zoneI18 || '').trim();
          if (!z) return;

          rows.forEach((r) => {
            out.push({
              row_key: r.key,
              g,
              z,
              i18,
              v: value,
              fileObj,
              fileMode,
            });
          });
        });
      });

      return out;
    }

    _serviceForGroup(group) {
      const g = String(group || '').trim();
      return this.data.find((x) => String(x.group || '').trim() === g) || null;
    }

    _serviceVariantIdForGroup(group) {
      const rec = this._serviceForGroup(group);
      const vid = rec?.product?.variant_id;
      const n = Number(vid);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    _serviceHandleForGroup(group) {
      const rec = this._serviceForGroup(group);
      return String(rec?.product?.handle || '').trim();
    }

    _zonesI18ByGroupFromSelections(selections) {
      const map = new Map();
      selections.forEach((s) => {
        const g = String(s.group || '').trim();
        const z = String(s.zone_i18 || '').trim();
        if (!g || !z) return;
        if (!map.has(g)) map.set(g, new Set());
        map.get(g).add(z);
      });
      return map;
    }

    _zonesI18ForBucket(bucket, zonesByGroup) {
      const out = new Set();
      const groups = Array.isArray(bucket?.groups) ? bucket.groups : [];
      groups.forEach((g) => {
        const set = zonesByGroup.get(String(g || '').trim());
        if (!set) return;
        set.forEach((z) => out.add(z));
      });
      return Array.from(out);
    }

    _buildVariantTableJSON_PrintEditor(rows, rowValues) {
      const byRow = new Map();
      rowValues.forEach((x) => {
        if (!byRow.has(x.row_key)) byRow.set(x.row_key, []);
        byRow.get(x.row_key).push(x);
      });

      const rs = rows.map((r) => {
        const inptsRaw = byRow.get(r.key) || [];
        const inpts = inptsRaw.map((x) => ({
          g: x.g,
          h: this._serviceHandleForGroup(x.g) || '',
          v: x.v,
        }));
        return {
          key: r.key,
          v_id: String(r.variant_id || ''),
          base_h: '',
          inpts,
        };
      });

      const out = {
        hdl: this.baseHandle,
        hdls: [this.baseHandle],
        rs,
      };

      if (this.clubCollection?.handle) {
        out.club_collection = {
          handle: this.clubCollection.handle,
          title: this.clubCollection.title || ''
        };
      }

      return JSON.stringify(out);
    }
    _formatPrice(cents) {
      const euros = (cents / 100).toFixed(2).replace('.', ',');
      return `${euros} ${this.i18n.currency}`;
    }

    _calculateServiceTotal() {
      const selections = this._collectSelections();
      const rowsCount = this._bulkItems.length > 0
        ? this._buildRowsFromBulk(this._bulkItems).length
        : (Array.isArray(this._rows) ? this._rows.length : 0);
      if (!selections.length || rowsCount === 0) return 0;

      let total = 0;
      selections.forEach((s) => {
        const rec = this._serviceForGroup(s.group);
        const price = Number(rec?.product?.price || 0);
        total += price * rowsCount;
      });

      return total;
    }

    _updatePriceSummary() {
      const summaryEl = this.querySelector('[data-role="price-summary"]');
      if (!summaryEl) return;

      const serviceTotal = this._calculateServiceTotal();
      const articleTotal = this._calculateArticleTotal();
      const grandTotal = serviceTotal + articleTotal;

      const serviceEl = summaryEl.querySelector('[data-role="service-total"]');
      const articleEl = summaryEl.querySelector('[data-role="article-total"]');
      const grandEl = summaryEl.querySelector('[data-role="grand-total"]');

      if (serviceEl) serviceEl.textContent = this._formatPrice(serviceTotal);
      if (articleEl) articleEl.textContent = this._formatPrice(articleTotal);
      if (grandEl) grandEl.textContent = this._formatPrice(grandTotal);

      summaryEl.style.display = grandTotal > 0 ? '' : 'none';
    }

    _genBatchId() {
      return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    }

    async _removeAllProductItems({ productHandle = '' } = {}) {
      try {
        const cart = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        const targets = (cart.items || []).filter((li) => {
          if (li.handle === productHandle) return true;

          const p = li.properties || {};
          if (p._LinkedToHandle === productHandle) return true;

          if (p._LinkedHandles) {
            try {
              const arr = JSON.parse(p._LinkedHandles);
              if (Array.isArray(arr) && arr.includes(productHandle)) return true;
            } catch (_) {}
          }

          return false;
        });

        for (const li of targets) {
          await fetch('/cart/change.js', {
            ...jsonFetchConfig('POST'),
            body: JSON.stringify({ id: li.key, quantity: 0 }),
          }).then((res) => (res.ok ? null : res.text().then((t) => Promise.reject(t))));
        }

        return targets.length;
      } catch (err) {
        this._warn('remove all product items failed', err);
        return 0;
      }
    }

    async _removeServiceItems({ tableHandle = '', batchId = '' } = {}) {
      try {
        const cart = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        const targets = (cart.items || []).filter((li) => {
          const p = li.properties || {};

          if (batchId && p._MpeBatch === batchId) return true;

          if (!batchId && tableHandle) {
            const hasServiceProps = p._LinkedToHandle || p._LinkedHandles || p._ServiceIndex || p._ServiceUUID;

            if (hasServiceProps) {
              if (p._LinkedToHandle === tableHandle) return true;

              if (p._LinkedHandles) {
                try {
                  const arr = JSON.parse(p._LinkedHandles);
                  if (Array.isArray(arr) && arr.includes(tableHandle)) return true;
                } catch (_) {}
              }
            }
          }

          return false;
        });

        for (const li of targets) {
          await fetch('/cart/change.js', {
            ...jsonFetchConfig('POST'),
            body: JSON.stringify({ id: li.key, quantity: 0 }),
          }).then((res) => (res.ok ? null : res.text().then((t) => Promise.reject(t))));
        }

        return targets.length;
      } catch (err) {
        this._warn('remove service items failed', err);
        return 0;
      }
    }

    _openCartDrawerFallback() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.assign('/cart');
    }

    _sectionsToRender() {
      return [
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#shopify-section-cart-icon-bubble' },
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      ];
    }

    async _fetchSectionsHTML(sections, sectionsUrl) {
      const url = new URL(sectionsUrl || window.location.href);
      url.searchParams.set('sections', sections.join(','));

      const resp = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const result = {};

      sections.forEach((name) => {
        const el = doc.querySelector(`#shopify-section-${name}`);
        if (el) result[name] = el.outerHTML;
      });

      return { sections: result, items: [] };
    }

    _renderSectionsToDOM(parsedState) {
      if (!parsedState || !parsedState.sections) return;

      this._sectionsToRender().forEach(({ id, selector, section }) => {
        const sectionHTML = parsedState.sections[section];
        if (!sectionHTML || typeof sectionHTML !== 'string') return;

        let container = document.getElementById(id);
        if (!container && section === 'cart-drawer') {
          container =
            document.querySelector('#CartDrawer .drawer__inner')?.closest('cart-drawer') ||
            null;
        }

        const dom = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const frag = dom.querySelector(selector);
        if (!frag) return;

        if (section === 'cart-drawer') {
          const target = document.querySelector('#CartDrawer .drawer__inner');
          if (target) target.innerHTML = frag.innerHTML;

          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.classList.toggle('is-empty', (parsedState.items || []).length === 0);
        } else if (container) {
          const target = container.querySelector(selector) || container;
          target.innerHTML = frag.innerHTML;
        }
      });
    }

    async _refreshCartSections() {
      const sections = this._sectionsToRender().map((s) => s.section);
      const sections_url = window.location.pathname + window.location.search;
      const updateUrl = window.routes?.cart_update_url || '/cart/update.js';

      try {
        const resp = await fetch(updateUrl, {
          ...jsonFetchConfig('POST'),
          body: JSON.stringify({ updates: {}, sections, sections_url }),
        });

        const parsed = await resp.json();
        this._renderSectionsToDOM(parsed);
        return parsed;
      } catch (e) {
        this._warn('refresh cart sections failed, using fallback', e);
        const fallback = await this._fetchSectionsHTML(sections, sections_url);
        this._renderSectionsToDOM(fallback);
        return fallback;
      }
    }

    _publishCartEvents(parsedState) {
      if (!hasPub()) return;
      try {
        window.publish(EVENTS().cartUpdate, { source: 'club-print-mini-editor', cartData: parsedState });
        window.publish(EVENTS().quantityUpdate, { source: 'club-print-mini-editor' });
      } finally {
        window.publish(EVENTS().cartRequestEnd);
      }
    }

    _dryRunLog(label, payload) {
      console.groupCollapsed(`%c[club-print] DRY-RUN: ${label}`, 'font-weight:600;color:#1976d2;');
      try {
        console.log(payload);
      } finally {
        console.groupEnd();
      }
    }

    async applyToCart({ dryRun = false } = {}) {
      if (this._busy) return;

      const rows = this._bulkItems.length > 0
        ? this._buildRowsFromBulk(this._bulkItems)
        : (Array.isArray(this._rows) ? this._rows : []);

      if (!rows.length) {
        this._setHint(this._bulkItems.length > 0 ? 'Keine Artikel ausgewählt' : this.i18n.hintNoRows, 'warning');
        return;
      }

      const selections = this._collectSelections();
      const hasBulk = this._bulkItems.length > 0;

      if (!selections.length && !hasBulk) {
        this._setHint(this.i18n.hintNoSelections, 'warning');
        return;
      }

      const rowValues = this._collectRowValues();
      const selectedGroups = new Set(selections.map((s) => s.group));
      const needAnyInput = [...selectedGroups].some((g) => {
        const rec = this._serviceForGroup(g);
        return rec && !rec.zone_only;
      });

      if (needAnyInput) {
        const filledGroups = new Set(rowValues.map(v => v.g));
        const missingGroups = [...selectedGroups].filter(g => {
          const rec = this._serviceForGroup(g);
          if (!rec || rec.zone_only) return false;
          // ── NEW: boolean is always filled ('false' is a valid answer) ─────
          if (String(rec.type || '').toLowerCase() === 'boolean') return false;
          // ──────────────────────────────────────────────────────────────────
          return !filledGroups.has(g);
        });

        if (missingGroups.length) {
          const labels = missingGroups.map(g => {
            const rec = this._serviceForGroup(g);
            return rec?.label || g;
          });

          this._setHint(`Bitte ausfüllen: <b>${labels.join(', ')}</b>`, 'validation');
          return;
        }
      }

      const tableJson = this._buildVariantTableJSON_PrintEditor(rows, rowValues);

      this._busy = true;
      this.classList.add('is-busy');
      this._setHint(this.i18n.hintSaving, 'saving');

      try {
        this._batchId = this._genBatchId();
        const linkedHandles = [this.baseHandle];
        const zonesByGroup = this._zonesI18ByGroupFromSelections(selections);

        if (hasPub()) window.publish(EVENTS().cartRequestStart);

        if (this._bulkItems.length > 0) {
          const physicalFd = new FormData();

          this._bulkItems.forEach((bulkItem, idx) => {
            physicalFd.append(`items[${idx}][id]`, String(bulkItem.variant_id));
            physicalFd.append(`items[${idx}][quantity]`, String(bulkItem.quantity));
            physicalFd.append(`items[${idx}][properties][_MpeBatch]`, this._batchId);
          });

          if (dryRun) {
            this._dryRunLog('Physical items FormData', physicalFd);
          } else {
            const physicalResponse = await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              body: physicalFd,
            });

            if (!physicalResponse.ok) {
              const t = await physicalResponse.text().catch(() => '');
              throw new Error(`Physical items add failed: ${physicalResponse.status} ${t}`);
            }
          }
        }

        if (this.addServices && selections.length) {
          let agg = this._aggregateServiceItemsFromSelections(selections);

          if (agg.length) {
            agg = this._sortAggByConfigOrder(agg);

            // Build per-group data map — now includes boolValue
            const groupDataMap = new Map();
            rowValues.forEach((rv) => {
              if (!groupDataMap.has(rv.g)) {
                groupDataMap.set(rv.g, { fileObj: null, url: '', textValue: '', boolValue: null });
              }
              const entry = groupDataMap.get(rv.g);

              if ((rv.fileMode === 'upload' || rv.fileMode === 'once') && rv.fileObj) {
                entry.fileObj = rv.fileObj;
              } else if (rv.fileMode === 'reference' && rv.v) {
                entry.url = rv.v;
              } else if (rv.fileMode === 'once-text' && rv.v) {
                entry.textValue = rv.v;
              } else if (rv.fileMode === 'once-boolean') {
                // ── NEW ─────────────────────────────────────────────────────
                entry.boolValue = rv.v; // 'true' or 'false'
              }
            });

            for (let idx = 0; idx < agg.length; idx++) {
              const b = agg[idx];
              const bucketZones = this._zonesI18ForBucket(b, zonesByGroup);

              const serviceFd = new FormData();
              serviceFd.append(`items[0][id]`, String(b.id));
              serviceFd.append(`items[0][quantity]`, String(b.quantity));
              serviceFd.append(
                `items[0][properties][_MpeBatch]`,
                `${this._batchId}-${idx}-${Math.random().toString(36).slice(2)}`
              );

              serviceFd.append(`items[0][properties][_ServiceIndex]`, String(idx));
              serviceFd.append(
                `items[0][properties][_ServiceUUID]`,
                `${this._batchId}-${idx}-${crypto.randomUUID()}`
              );

              serviceFd.append(`items[0][properties][_LinkedToHandle]`, this.baseHandle);
              serviceFd.append(`items[0][properties][_LinkedHandles]`, JSON.stringify(linkedHandles));
              serviceFd.append(`items[0][properties][_Handle]`, b.handle || '');

              if (bucketZones.length) {
                serviceFd.append(`items[0][properties][_ZonesI18]`, JSON.stringify(bucketZones));
              }

              const isLastService = idx === agg.length - 1;
              if (isLastService) {
                serviceFd.append(`items[0][properties][_VariantTableJSON]`, tableJson);

                groupDataMap.forEach((entry, group) => {
                  if (entry.fileObj) {
                    const propName = this._propNameForGroup(group);
                    if (propName) serviceFd.append(`items[0][properties][${propName}]`, entry.fileObj, entry.fileObj.name);
                  } else if (entry.url) {
                    const propName = this._propNameForGroup(group);
                    if (propName) serviceFd.append(`items[0][properties][${propName}]`, entry.url);
                  } else if (entry.textValue) {
                    const textPropName = this._textPropNameForGroup(group);
                    serviceFd.append(`items[0][properties][${textPropName}]`, entry.textValue);
                  } else if (entry.boolValue !== null) {
                    // ── NEW: boolean sent as text property ('true'/'false') ──
                    const textPropName = this._textPropNameForGroup(group);
                    serviceFd.append(`items[0][properties][${textPropName}]`, entry.boolValue);
                  }
                });
              }

              if (dryRun) {
                this._dryRunLog(`Service ${idx} FormData`, serviceFd);
              } else {
                const serviceResponse = await fetch('/cart/add.js', {
                  method: 'POST',
                  credentials: 'same-origin',
                  body: serviceFd,
                });

                if (!serviceResponse.ok) {
                  const t = await serviceResponse.text().catch(() => '');
                  throw new Error(`Service ${idx} add failed: ${serviceResponse.status} ${t}`);
                }

                if (idx < agg.length - 1) {
                  await new Promise(resolve => setTimeout(resolve, 150));
                }
              }
            }
          }
        }

        if (dryRun) {
          this._setHint(`${this.i18n.hintSaved} (dry-run)`, 'success');
          if (hasPub()) window.publish(EVENTS().cartRequestEnd);
          return;
        }

        const parsedState = await this._refreshCartSections();
        this._publishCartEvents(parsedState);

        this._setHint(this.i18n.hintSaved, 'success');

        this._resetBulkComponent();

        setTimeout(() => {
          this.reset();

          setTimeout(() => {
            this._scrollToBulkComponent();
          }, 100);
        }, 50);

        if (this.openDrawer) setTimeout(() => this._openCartDrawerFallback(), 300);

      } catch (e) {
        this._warn('apply failed', e);
        if (hasPub()) window.publish(EVENTS().cartRequestEnd);
        this._setHint(`${this.i18n.hintError} (${e.message})`, 'error');
      } finally {
        this._busy = false;
        this.classList.remove('is-busy');
      }
    }

    _aggregateServiceItemsFromSelections(selections) {
      const byVariant = new Map();
      const rowsCount = this._bulkItems.length > 0
        ? this._buildRowsFromBulk(this._bulkItems).length
        : (Array.isArray(this._rows) ? this._rows.length : 0);

      selections.forEach((s) => {
        const vid = this._serviceVariantIdForGroup(s.group);
        if (!vid || rowsCount === 0) return;

        const k = String(vid);
        if (!byVariant.has(k)) {
          byVariant.set(k, {
            id: vid,
            quantity: 0,
            groups: new Set(),
            handle: this._serviceHandleForGroup(s.group) || '',
          });
        }

        const b = byVariant.get(k);
        b.quantity += rowsCount;
        b.groups.add(s.group);
      });

      return Array.from(byVariant.values()).map((x) => ({
        id: x.id,
        quantity: x.quantity,
        groups: Array.from(x.groups),
        handle: x.handle || '',
      }));
    }

    _sortAggByConfigOrder(agg) {
      const idxByGroup = new Map(
        this.data.map((rec, idx) => [String(rec?.group || '').trim(), idx])
      );

      const score = (b) => {
        const gs = Array.isArray(b?.groups) ? b.groups : [];
        let max = -1;
        gs.forEach((g) => {
          const i = idxByGroup.get(String(g || '').trim());
          if (typeof i === 'number' && i > max) max = i;
        });
        return max;
      };

      return agg.slice().sort((a, b) => score(a) - score(b));
    }

    _propNameForGroup(group) {
      const g = String(group || '').trim().toLowerCase();

      const known = {
        sponsor: '_SponsorURL',
        brustsponsor: '_BrustsponsorURL',
        vereinslogo: '_VereinslogoURL',
        logo: '_LogoURL',
        teamlogo: '_TeamlogoURL',
      };
      if (known[g]) return known[g];

      const m = g.match(/^logo[\s\-_]*([0-9]+)$/i);
      if (m) return `_Logo${m[1]}URL`;

      const pascal = g
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim()
        .split(/\s+/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
        .join('');

      return `_${pascal || 'File'}URL`;
    }

    _textPropNameForGroup(group) {
      const g = String(group || '').trim().toLowerCase();

      const known = {
        name: '_Name',
        nummer: '_Nummer',
        number: '_Number',
        text: '_Text',
        initialen: '_Initialen',
        initials: '_Initials',
      };
      if (known[g]) return known[g];

      const pascal = g
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim()
        .split(/\s+/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
        .join('');

      return `_${pascal || 'Value'}`;
    }

    _setHint(message, type = 'info') {
      this._hintState = type;
      const el = this._el('hint');
      if (!el) return;

      el.innerHTML = message || '';

      el.classList.remove('hint-info', 'hint-warning', 'hint-validation', 'hint-saving', 'hint-success', 'hint-error');

      if (type) {
        el.classList.add(`hint-${type}`);
      }

      if (type === 'success') {
        setTimeout(() => {
          if (this._hintState === 'success') {
            this._clearHint();
            this._updateUIState();
          }
        }, 2000);
      }

      if (type === 'validation') {
        setTimeout(() => {
          if (this._hintState === 'validation') {
            this._clearHint();
            this._updateUIState();
          }
        }, 4000);
      }
    }

    _clearHint() {
      this._hintState = 'idle';
      this._setHint('');
    }

    _updateUIState() {
      const resetBtn = this.querySelector('[data-action="reset"]');
      const hintEl = this._el('hint');

      const hasPhysical = this._bulkItems.length > 0 || (Array.isArray(this._rows) && this._rows.length > 0);

      const hasNonFixedZones = Array.from(this.querySelectorAll('.cpm__cb:checked')).some(cb => {
        const groupEl = cb.closest('.cpm__group');
        const isFixed = groupEl?.dataset.zoneOnly === 'true' && groupEl?.dataset.zoneChecked === 'true';
        return !isFixed;
      });

      const hasTextValues = Array.from(
        this.querySelectorAll('.cpm__row-field-input, [data-role="once-input"]')
      ).some(el => String(el.value || '').trim() !== '');

      const hasFiles = Array.from(
        this.querySelectorAll('input[type="file"]')
      ).some(el => el.files && el.files.length);

      // ── NEW: boolean "touched" only when checked (unchecked = default state)
      const hasBooleans = Array.from(
        this.querySelectorAll('[data-role="once-boolean-input"], .cpm__row-field-boolean')
      ).some(el => el.checked);

      const hasPersonalization = hasNonFixedZones || hasTextValues || hasFiles || hasBooleans;

      if (resetBtn) {
        resetBtn.style.display = hasPersonalization ? '' : 'none';
      }

      if (!hintEl) return;

      const isSystemMessage = ['validation', 'saving', 'error'].includes(this._hintState);
      if (isSystemMessage) return;

      if (hasPhysical && !hasNonFixedZones && !hasTextValues && !hasFiles && !hasBooleans) {
        const hasFixedZones = Array.from(this.querySelectorAll('.cpm__cb:checked')).some(cb => {
          const groupEl = cb.closest('.cpm__group');
          const isFixed = groupEl?.dataset.zoneOnly === 'true' && groupEl?.dataset.zoneChecked === 'true';
          return isFixed;
        });

        if (!hasFixedZones) {
          this._setHint('In den Warenkorb legen Artikel <b>ohne Personalisierung</b>', 'info');
        } else {
          if (this._hintState === 'info' || this._hintState === 'idle') {
            this._clearHint();
          }
        }
      } else {
        if (this._hintState === 'info' || this._hintState === 'idle') {
          this._clearHint();
        }
      }
    }
      _saveRowState() {
        const state = {};

        this.querySelectorAll('.cpm__row-field-input').forEach(el => {
          const key = `${el.dataset.rowKey}_${el.dataset.group}_${el.dataset.zone}`;
          state[key] = el.value;
        });

        this.querySelectorAll('.cpm__row-field-boolean').forEach(el => {
          const key = `${el.dataset.rowKey}_${el.dataset.group}_${el.dataset.zone}`;
          state[key] = el.checked;
        });

        return state;
      }
      _restoreRowState(state) {
        if (!state) return;

        this.querySelectorAll('.cpm__row-field-input').forEach(el => {
          const key = `${el.dataset.rowKey}_${el.dataset.group}_${el.dataset.zone}`;
          if (state[key] !== undefined) {
            el.value = state[key];
          }
        });

        this.querySelectorAll('.cpm__row-field-boolean').forEach(el => {
          const key = `${el.dataset.rowKey}_${el.dataset.group}_${el.dataset.zone}`;
          if (state[key] !== undefined) {
            el.checked = state[key];
          }
        });
      }
    _calculateArticleTotal() {
      if (!this._bulkItems.length) return 0;

      let total = 0;

      this._bulkItems.forEach(item => {
        const price = Number(item.price || 0);
        const qty = Number(item.quantity || 0);
        total += price * qty;
      });

      return total;
    }

    _scrollToBulkComponent() {
      const bulkComponent = document.querySelector('add-to-cart-bulk');
      if (bulkComponent) {
        const offsetTop = bulkComponent.offsetTop - 20;

        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    }

    _resetBulkComponent() {
      const bulkComponent = document.querySelector('add-to-cart-bulk');
      if (bulkComponent && typeof bulkComponent.reset === 'function') {
        bulkComponent.reset();
      }
    }

    _setStatus(txt) {
      const el = this._el('status');
      if (el) el.textContent = String(txt || '');
    }

    _warn(msg, obj) {
      try {
        console.warn(`[club-print] ${msg}`, obj ?? '');
      } catch (_) {}
    }
  }

  if (!customElements.get('club-print-mini-editor')) {
    customElements.define('club-print-mini-editor', ClubPrintMiniEditor);
  }
})();